import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, limit as fsLimit, orderBy, query, where } from "firebase/firestore";

import { db } from "../lib/firebase";
import { useCollection } from "../hooks/useCollection";
import { useCallableAction } from "../hooks/useCallableAction";
import type {
  SendOrdersToProductionInput,
  SendOrdersToProductionResult,
} from "../hooks/callableResults";
import { useToast } from "../components/Toast";
import { Modal } from "../components/Modal";
import { LoadingBlock, Spinner } from "../components/Spinner";
import { EmptyState } from "../components/EmptyState";
import { ChannelBadge, channelOf } from "../components/ChannelBadge";
// Kalem listesi Siparişler ekranıyla PAYLAŞILIR — ikinci bir kopya, kalem
// görseli / donmuş fiyat / silinmiş ürün rozeti kurallarını ayrıştırırdı.
import { OrderItems } from "../pages/Orders";
import { FastShippingBadge } from "../components/FastShippingBadge";
import { tl, n, date, since } from "../lib/format";

/**
 * ============================================================================
 * ÜRETİMİ BEKLEYEN KUYRUĞU — onaylanmış ama HENÜZ ÜRETİME GÖNDERİLMEMİŞ sipariş
 * ============================================================================
 *
 * ── NİÇİN BU EKRAN VAR ─────────────────────────────────────────────────────
 * "Üretimi Bekleyen" bir ÜRETİM AŞAMASI DEĞİL, bir KUYRUK GÖRÜNÜMÜDÜR. Atölye
 * yakında hangi işlerin geleceğini görmek zorunda; ama bunu görmek için üretim
 * kaydının AÇILMIŞ olması gerekmiyor — açılmış olsaydı iş fiilen üretime girmiş
 * olurdu.
 *
 * ── BUGÜN NE OLUYORDU (ölçüm 2026-08-16) ───────────────────────────────────
 * "Üretimi Bekleyen" sekmesi `production` koleksiyonunda `status == "prod-hold"`
 * sayıyordu: **0 kayıt**. Siparişler → "Onaylandı" (`orders.erpStatus ==
 * "wc-processing"`) ise **15 kayıt**. Yani atölye, önündeki 15 işin hiçbirini
 * göremiyordu; ekran boş olduğu için "iş yok" diyordu. Sıfır ile "bilinmiyor"
 * arasındaki fark değil, sıfır ile GERÇEK arasındaki fark kaybolmuştu.
 *
 * ── ★ ÇÖZÜMÜN SINIRI — NİÇİN ÜRETİM KAYDI AÇILMIYOR ────────────────────────
 * Bu ekran sipariş OKUR; hiçbir üretim kaydı OLUŞTURMAZ. `statuses/wc-processing`
 * üzerindeki `opensProduction` alanı `false` KALIR ve kalmalıdır.
 *
 * Bugün erken saatte tam tersi denendi: sipariş onaylanınca üretim kaydı
 * otomatik açılsın diye `opensProduction` true yapıldı. Sonuç: aynı sipariş hem
 * "Onaylandı" hem "CNC" sekmesinde göründü ve 21 hatalı üretim kaydı elle
 * silinmek zorunda kaldı. İş sahibinin kuralı nettir: "ben onaylıdan üretime
 * göndermediğim sürece üretimde hiçbir şey çıkmasın."
 *
 * İki istek çelişmiyor çünkü GÖRMEK ile ÜRETİME ALMAK ayrı şeylerdir:
 *   · Bu kuyruk  → `orders` okur, yazmaz. Atölye ne geleceğini görür.
 *   · Planlama…Paketleme → `production` okur. Oraya YALNIZCA "Üretime Gönder" düşürür.
 *
 * ── ★ SÜZGEÇ NİÇİN SADECE `erpStatus` ──────────────────────────────────────
 * "Üretim kaydı olmayan sipariş" için akla ilk gelen süzgeç `productionId == ""`
 * idi. CANLI ÖLÇÜM BUNU ÇÜRÜTTÜ: onaylı 15 siparişin 4'ünde (#DMNTH1028TR,
 * 1029, 1030, 1031) `productionId` ve `productionStatus: "prod-cnc"` HÂLÂ
 * yazılıydı — bugün silinen 21 hatalı üretim kaydından kalan ölü işaretçiler.
 * `production/order_674136…` dokümanlarının hiçbiri yok (varlık kontrolüyle
 * doğrulandı). O süzgeçle bu 4 gerçek iş kuyruktan SESSİZCE düşerdi.
 *
 * Yetkili kaynak `erpStatus`tır: `sendOrderToProduction` üretim kaydını AÇTIĞI
 * anda siparişi `wc-uretimde`ye taşır (`functions/src/production/send.ts:238`).
 * Yani "hâlâ `wc-processing`" demek "üretime gönderilmedi" demektir — bayat bir
 * kopya alan değil, işlemin kendisinin bıraktığı iz.
 *
 * ── SAYIM ──────────────────────────────────────────────────────────────────
 * Kart adedi `useStatusCounts` (→ `getCountFromServer`) ile CANLI sayılır.
 * `counters/*` dokümanları KULLANILMAZ: onları yazan hiçbir kod yoktu, kart
 * "172 kayıt" derken listede 7 satır vardı ve bugün silindiler.
 */

/** Siparişler → "Onaylandı". Sözlükteki teknik ad; düz "processing" DEĞİL. */
export const APPROVED_ORDER_STATUS = "wc-processing";

/**
 * Liste üst sınırı. Onaylı kuyruk canlıda 15 satır; 200 hem fazlasıyla yeter
 * hem de atölye tabletini kilitlemez. Sınıra dayanılırsa kullanıcıya YAZILIR —
 * sessizce kesilen liste "iş bitti" sanılır.
 */
const MAX_ROWS = 200;

interface ApprovedOrderDoc {
  orderName?: string;
  customerFullName?: string;
  channel?: string | null;
  trendyol?: unknown;
  shopifyOrderId?: string | null;
  /** Kalemlerden biri stoktan çıkıyorsa true — 1 iş gününde gönderilir. */
  fastShipping?: boolean;
  lineItemCount?: number;
  totalQuantity?: number;
  totals?: { total?: number };
  createdAt?: { toDate: () => Date } | null;
}

export function PendingOrdersQueue() {
  const toast = useToast();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** Kalemleri açık olan satırlar — Siparişler ekranındaki desenin aynısı. */
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);

  /**
   * İŞ KURALI TEK GÖVDEDE: Siparişler ekranındaki düğmeyle BİREBİR aynı
   * callable. Buraya ikinci bir uç yazmak, ödeme kapısı (havale/EFT kontrolü)
   * ve deterministik kimlik gibi kuralların iki yerde ayrışması demekti.
   */
  const sendAction = useCallableAction<
    SendOrdersToProductionInput,
    SendOrdersToProductionResult
  >("sendOrdersToProduction");

  /**
   * Sorgu `orders` (erpStatus, createdAt desc) bileşik indeksini kullanır —
   * Siparişler ekranı aynı sorguyu attığı için indeks zaten tanımlı.
   */
  const q = useCollection<ApprovedOrderDoc>(
    () =>
      query(
        collection(db, "orders"),
        where("erpStatus", "==", APPROVED_ORDER_STATUS),
        orderBy("createdAt", "desc"),
        fsLimit(MAX_ROWS),
      ),
    [],
  );

  const rows = q.docs;
  const atLimit = rows.length >= MAX_ROWS;

  // Seçim listeden düşen kayıtları taşımasın: sipariş üretime gidince satır
  // canlı dinleyiciden kalkar, seçim kümesinde ölü kimlik kalırdı.
  const visibleIds = useMemo(() => new Set(rows.map((o) => o.id)), [rows]);
  const liveSelected = useMemo(
    () => [...selected].filter((id) => visibleIds.has(id)),
    [selected, visibleIds],
  );

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /**
   * Toplu gönderim. Sonuç SATIR SATIR değerlendirilir — sabit "başarıyla
   * gönderildi" mesajı YOK. `created:false` bir hata değil, "zaten üretimde"
   * demektir ve ayrıca yazılır; `!ok` satırların sebebi sunucudan Türkçe gelir
   * (ör. ödemesi tamamlanmamış havale siparişi).
   */
  const runSend = async () => {
    const ids = liveSelected;
    setConfirmOpen(false);
    if (ids.length === 0) return;

    const out = await sendAction.run({ orderIds: ids });
    if (!out) {
      toast.error(sendAction.error ?? "Üretime gönderilemedi.");
      return;
    }

    const failedRows = out.results.filter((r) => !r.ok);
    const already = out.results.filter((r) => r.ok && !r.created).length;
    const alreadyNote =
      already > 0 ? `${already} sipariş zaten üretimdeydi, yeniden açılmadı.` : null;

    if (failedRows.length > 0) {
      toast.error(
        `${out.created} sipariş üretime gönderildi, ${failedRows.length} sipariş gönderilemedi.`,
        [
          ...(alreadyNote ? [alreadyNote] : []),
          ...failedRows.map((r) => `${r.orderId}: ${r.error ?? "Sebep bildirilmedi."}`),
        ].join("\n"),
      );
    } else {
      toast.success(
        `${out.created} sipariş üretime gönderildi. Artık Planlama akışında.`,
        alreadyNote ?? undefined,
      );
    }
    setSelected(new Set());
  };

  const allSelected = rows.length > 0 && rows.every((o) => selected.has(o.id));

  return (
    <section aria-labelledby="pending-orders-heading" className="space-y-3">
      <div>
        <h2 id="pending-orders-heading" className="text-lg font-semibold text-gray-900">
          Üretime gönderilmemiş onaylı siparişler
        </h2>

        {/* ★ EKRANDA NE OLDUĞU YAZAR. İki kavram (kuyruk ↔ üretim aşaması)
            karıştırıldığı için bugün 21 hatalı üretim kaydı oluştu. */}
        <p className="mt-1 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
          Bunlar Siparişler → <span className="font-semibold">Onaylandı</span> statüsündeki,{" "}
          <span className="font-semibold">henüz üretime gönderilmemiş</span> siparişlerdir. Burada
          görünmeleri üretime girdikleri anlamına <span className="font-semibold">gelmez</span> —
          üretim kaydı yoktur, kesim/ayak listesine düşmezler.{" "}
          <span className="font-semibold">Üretime Gönder</span> dendiğinde üretim kaydı açılır,
          sipariş "Üretimde"ye geçer, bu listeden düşer ve Planlama akışına girer.
        </p>
      </div>

      {/* ★ ARAÇ ÇUBUĞU HER ROLE AÇIK.
          Eskiden yalnızca ofis/patron görüyordu ve üretim rolüne "salt
          okunur" yazıyordu. İş akışının tarifi ise şu: ofis onaylar, İŞİ
          KUYRUKTAN ÜRETİM ALIR. Atölye işi göremiyor değil, BAŞLATAMIYORDU;
          ofisin gelip düğmeye basmasını bekliyordu. Sunucu da aynı anda
          `ALL_ROLES`e açıldı (`production/send.ts`) — ekran ile uç ayrışmasın. */}
      <div className="flex flex-wrap items-center gap-3 noprint">
          {rows.length > 0 ? (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                checked={allSelected}
                ref={(el) => {
                  if (el) {
                    const some = rows.some((o) => selected.has(o.id));
                    el.indeterminate = some && !allSelected;
                  }
                }}
                onChange={(e) => {
                  setSelected((prev) => {
                    const next = new Set(prev);
                    if (e.target.checked) rows.forEach((o) => next.add(o.id));
                    else rows.forEach((o) => next.delete(o.id));
                    return next;
                  });
                }}
              />
              Tümünü seç
            </label>
          ) : null}

          <span className="text-sm text-gray-600">
            {liveSelected.length > 0
              ? `${liveSelected.length} sipariş seçili`
              : "Göndermek için sipariş seçin"}
          </span>

          <button
            type="button"
            className="btn-primary"
            disabled={liveSelected.length === 0 || sendAction.pending}
            onClick={() => setConfirmOpen(true)}
          >
            Üretime Gönder
          </button>
        {sendAction.pending ? <Spinner size="sm" label="Gönderiliyor" /> : null}
      </div>

      {q.loading ? (
        <LoadingBlock label="Onaylı siparişler yükleniyor" />
      ) : q.error ? (
        // Sessiz sıfır YOK: sayılamadı ile "iş yok" ayrı gösterilir.
        <div
          role="alert"
          className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          Onaylı siparişler okunamadı: {q.error} — bu <em>“bekleyen iş yok”</em> demek değildir.
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="Üretime gönderilmemiş onaylı sipariş yok"
          description="Siparişler ekranında bir sipariş 'Onaylandı' statüsüne geçtiğinde burada görünür."
        />
      ) : (
        <>
          {atLimit ? (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Liste ilk {MAX_ROWS} kayıtta kesildi; daha fazlası olabilir. Tamamı için Siparişler →
              Onaylandı ekranını kullanın.
            </p>
          ) : null}

          <ul role="list" className="space-y-3">
            {rows.map((o) => {
              const isSelected = selected.has(o.id);
              const isOpen = expanded.has(o.id);
              return (
                <li
                  key={o.id}
                  className={`card ${isSelected ? "ring-2 ring-primary-500" : ""}`}
                >
                  <div className="flex flex-wrap items-center gap-3 p-3 sm:p-4">
                    {/* TABLET: 44px dokunma hedefi — atölye ekranı parmakla
                        kullanılıyor, 16px checkbox ıskalanıyordu. */}
                      <label className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center noprint">
                        <span className="sr-only">{`${o.orderName ?? o.id} siparişini seç`}</span>
                        <input
                          type="checkbox"
                          className="h-6 w-6 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          checked={isSelected}
                          onChange={() => toggle(o.id)}
                        />
                      </label>

                    {/* ★ SATIRIN KENDİSİ KALEMLERİ AÇAR, OK DETAYA GİDER.
                        Siparişler ekranındaki davranışın aynısı: atölye hangi
                        ürünlerin geldiğini sayfa değiştirmeden görebilmeli.
                        Eskiden satıra basmak doğrudan yeni sayfa açıyor,
                        listedeki yer kayboluyordu. */}
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      onClick={() => toggleExpand(o.id)}
                      className="min-w-0 flex-1 rounded-md p-1 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                    >
                      <span className="block text-base font-semibold text-gray-900">
                        {o.customerFullName?.trim() || "İsimsiz müşteri"}
                      </span>
                      <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                        {/* Trendyol'un kargolama penceresi dar — öncelik farkı
                            ilk bakışta görünmeli. */}
                        <ChannelBadge channel={channelOf(o)} />
                        <span className="font-mono">{o.orderName ?? o.id}</span>
                        <FastShippingBadge fastShipping={o.fastShipping} />
                        <span>
                          {n(o.lineItemCount ?? 0, " kalem")}
                          {o.totalQuantity ? ` · ${n(o.totalQuantity, " adet")}` : ""}
                        </span>
                        <span>{since(o.createdAt)}</span>
                      </span>
                    </button>

                    <div className="hidden text-right sm:block">
                      {/* Tutar üç role de açıktır; gizli olan maliyet ve kârdır. */}
                      <p className="text-sm tabular-nums text-gray-900">{tl(o.totals?.total)}</p>
                      <p className="mt-0.5 text-xs text-gray-500">{date(o.createdAt)}</p>
                    </div>

                    {/* Ok = yeni sayfa. Siparişler ekranındaki simgenin aynısı. */}
                    <Link
                      to={`/orders/${encodeURIComponent(o.id)}`}
                      className="shrink-0 rounded p-1 text-gray-600 hover:text-gray-700 noprint"
                      aria-label={`${o.orderName ?? o.id} sipariş detayını aç`}
                    >
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path
                          fillRule="evenodd"
                          d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </Link>
                  </div>

                  {/* Dar ekranda tutar/tarih alt satıra iner */}
                  <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2 text-xs text-gray-500 sm:hidden">
                    <span>{date(o.createdAt)}</span>
                    <span className="tabular-nums text-gray-900">{tl(o.totals?.total)}</span>
                  </div>

                  {/* ★ AYNI BİLEŞEN — Siparişler ekranı da bunu kullanır.
                      İkinci bir kalem listesi yazmak, kalem görseli / donmuş
                      fiyat / silinmiş ürün rozeti gibi kuralların iki yerde
                      ayrışması demekti. */}
                  {isOpen ? (
                    <div className="border-t border-gray-100 bg-gray-50">
                      <OrderItems orderId={o.id} />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </>
      )}

      <Modal
        open={confirmOpen}
        title="Üretime gönder"
        description={
          `${liveSelected.length} sipariş için üretim kaydı açılacak ve siparişler "Üretimde" ` +
          `statüsüne geçecek. Bu listeden düşerler. Ödemesi tamamlanmamış siparişler ` +
          `gönderilmez; sebebi satır satır bildirilir.`
        }
        onClose={() => setConfirmOpen(false)}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setConfirmOpen(false)}>
              Vazgeç
            </button>
            <button type="button" className="btn-primary" onClick={() => void runSend()}>
              Onayla
            </button>
          </>
        }
      />
    </section>
  );
}

export default PendingOrdersQueue;
